export default function customSelect(config = {}) {
    return {
        open: false,
        search: '',
        value: config.initialValue,
        options: Array.isArray(config.options) ? config.options : [],
        multiple: Boolean(config.multiple),
        placeholder: config.placeholder || '',
        activeIndex: -1,

        init() {
            this.normalizeValue();

            this.$watch('search', () => {
                this.activeIndex = -1;
            });

            this.$watch('open', (isOpen) => {
                if (isOpen) {
                    this.activeIndex = -1;
                    this.$nextTick(() => {
                        this.$refs.searchInput?.focus();
                    });
                } else {
                    this.search = '';
                }
            });
        },

        normalizeValue() {
            if (this.multiple) {
                if (!Array.isArray(this.value)) {
                    if (this.value === null || this.value === undefined || this.value === '') {
                        this.value = [];
                    } else {
                        this.value = [this.value];
                    }
                }
            } else {
                if (Array.isArray(this.value)) {
                    this.value = this.value.length > 0 ? this.value[0] : null;
                }
            }
        },

        get filteredOptions() {
            if (!this.search.trim()) {
                return this.options;
            }
            const query = this.search.trim().toLowerCase();
            return this.options.filter((opt) => String(opt.label).toLowerCase().includes(query));
        },

        get selectedOptions() {
            if (this.multiple) {
                const vals = Array.isArray(this.value) ? this.value : [];
                return vals.map((v) => {
                    const found = this.options.find((o) => String(o.value) === String(v));
                    return found || { value: v, label: v };
                });
            }
            if (this.value === null || this.value === undefined || this.value === '') {
                return null;
            }
            return (
                this.options.find((o) => String(o.value) === String(this.value)) || {
                    value: this.value,
                    label: this.value,
                }
            );
        },

        isSelected(val) {
            if (this.multiple) {
                return Array.isArray(this.value) && this.value.some((v) => String(v) === String(val));
            }
            return String(this.value) === String(val);
        },

        select(opt) {
            if (!opt || opt.disabled) return;
            const val = opt.value;

            if (this.multiple) {
                const arr = Array.isArray(this.value) ? [...this.value] : [];
                const idx = arr.findIndex((v) => String(v) === String(val));
                if (idx > -1) {
                    arr.splice(idx, 1);
                } else {
                    arr.push(val);
                }
                this.value = arr;
            } else {
                this.value = val;
                this.open = false;
                this.search = '';
            }
        },

        remove(val) {
            if (this.multiple && Array.isArray(this.value)) {
                this.value = this.value.filter((v) => String(v) !== String(val));
            } else {
                this.value = null;
            }
        },

        onKeyDown(e) {
            if (!this.open) {
                if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
                    e.preventDefault();
                    this.open = true;
                }
                return;
            }

            const opts = this.filteredOptions;
            if (!opts.length) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    this.navigateOptions(1);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    this.navigateOptions(-1);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (this.activeIndex >= 0 && this.activeIndex < opts.length) {
                        this.select(opts[this.activeIndex]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    this.open = false;
                    break;
                case 'Home':
                    e.preventDefault();
                    this.activeIndex = 0;
                    this.scrollToActive();
                    break;
                case 'End':
                    e.preventDefault();
                    this.activeIndex = opts.length - 1;
                    this.scrollToActive();
                    break;
            }
        },

        navigateOptions(direction) {
            const opts = this.filteredOptions;
            if (!opts.length) return;

            let newIdx = this.activeIndex + direction;
            if (newIdx < 0) newIdx = opts.length - 1;
            if (newIdx >= opts.length) newIdx = 0;

            let attempts = 0;
            while (opts[newIdx]?.disabled && attempts < opts.length) {
                newIdx += direction;
                if (newIdx < 0) newIdx = opts.length - 1;
                if (newIdx >= opts.length) newIdx = 0;
                attempts++;
            }

            this.activeIndex = newIdx;
            this.scrollToActive();
        },

        scrollToActive() {
            this.$nextTick(() => {
                const list = this.$refs.optionsList;
                if (!list) return;
                const activeEl = list.children[this.activeIndex];
                if (activeEl) {
                    activeEl.scrollIntoView({ block: 'nearest' });
                }
            });
        },
    };
}
